<i18n>
{
  "de": {
    "type": "Typ",
    "all": "Alle",
    "subscriptions": "Mitgliedschaften",
    "products": "Produkte",
    "events": "Events",
    "categories": "Kategorien",
    "allCategories": "Alle",
    "sortBy": "Sortieren nach",
    "date": "Datum",
    "price": "Preis",
    "name": "Name",
    "sortOrder": "Reihenfolge",
    "newestFirst": "Neueste zuerst",
    "oldestFirst": "Älteste zuerst",
    "priceAsc": "Preis aufsteigend",
    "priceDesc": "Preis absteigend",
    "nameAsc": "A - Z",
    "nameDesc": "Z - A"
  },
  "en": {
    "type": "Type",
    "all": "All",
    "subscriptions": "Memberships",
    "products": "Products",
    "events": "Events",
    "categories": "Categories",
    "allCategories": "All",
    "sortBy": "Sort by",
    "date": "Date",
    "price": "Price",
    "name": "Name",
    "sortOrder": "Order",
    "newestFirst": "Newest first",
    "oldestFirst": "Oldest first",
    "priceAsc": "Price ascending",
    "priceDesc": "Price descending",
    "nameAsc": "A - Z",
    "nameDesc": "Z - A"
  }
}
</i18n>

<template>
  <div class="filter-shop-content">
    <!-- Type Filter -->
    <div class="filter-section">
      <h4 class="section-title">
        <i class="icon-filter" />
        {{ $t('type') }}
      </h4>
      <div class="filter-chips">
        <button
          class="filter-chip"
          :class="{ 'filter-chip--active': localFilter.type === null }"
          @click="setType(null)"
        >
          {{ $t('all') }}
        </button>
        <button
          v-if="hasSubscriptions"
          class="filter-chip"
          :class="{ 'filter-chip--active': localFilter.type === 'subscription' }"
          @click="setType('subscription')"
        >
          {{ $t('subscriptions') }}
        </button>
        <button
          v-if="hasProducts"
          class="filter-chip"
          :class="{ 'filter-chip--active': localFilter.type === 'product' }"
          @click="setType('product')"
        >
          {{ $t('products') }}
        </button>
        <button
          v-if="hasEvents"
          class="filter-chip"
          :class="{ 'filter-chip--active': localFilter.type === 'event' }"
          @click="setType('event')"
        >
          {{ $t('events') }}
        </button>
      </div>
    </div>

    <!-- Category Filter (Mobile only - Desktop shows categories above product listing) -->
    <div
      v-if="isMobileView && showCategoryFilter"
      class="filter-section"
    >
      <h4 class="section-title">
        <i class="icon-tags" />
        {{ $t('categories') }}
      </h4>
      <div class="filter-chips">
        <button
          class="filter-chip"
          :class="{ 'filter-chip--active': !activeCategory }"
          @click="setCategory(null)"
        >
          {{ $t('allCategories') }}
        </button>
        <button
          v-for="category in availableCategories"
          :key="category.slug"
          class="filter-chip"
          :class="{ 'filter-chip--active': activeCategory === category.slug }"
          @click="setCategory(category.slug)"
        >
          {{ category.name }}
        </button>
      </div>
    </div>

    <!-- Sort By -->
    <div class="filter-section">
      <h4 class="section-title">
        <i class="icon-sliders" />
        {{ $t('sortBy') }}
      </h4>
      <div class="filter-chips">
        <button
          class="filter-chip"
          :class="{ 'filter-chip--active': localFilter.sortBy === 'date' }"
          @click="setSortBy('date')"
        >
          {{ $t('date') }}
        </button>
        <button
          class="filter-chip"
          :class="{ 'filter-chip--active': localFilter.sortBy === 'price' }"
          @click="setSortBy('price')"
        >
          {{ $t('price') }}
        </button>
        <button
          class="filter-chip"
          :class="{ 'filter-chip--active': localFilter.sortBy === 'name' }"
          @click="setSortBy('name')"
        >
          {{ $t('name') }}
        </button>
      </div>
    </div>

    <!-- Sort Order -->
    <div class="filter-section">
      <h4 class="section-title">
        <i class="icon-eye" />
        {{ $t('sortOrder') }}
      </h4>
      <div class="filter-chips">
        <button
          class="filter-chip"
          :class="{ 'filter-chip--active': localFilter.sortOrder === 'desc' }"
          @click="setSortOrder('desc')"
        >
          {{ sortOrderDescLabel }}
        </button>
        <button
          class="filter-chip"
          :class="{ 'filter-chip--active': localFilter.sortOrder === 'asc' }"
          @click="setSortOrder('asc')"
        >
          {{ sortOrderAscLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'FilterShopContent',

  data() {
    return {
      localFilter: {
        type: null,
        sortBy: 'date',
        sortOrder: 'desc'
      }
    }
  },

  computed: {
    ...mapState(['artist', 'shopFilter']),

    hasSubscriptions() {
      return this.artist?.rewards?.length > 0
    },

    hasProducts() {
      return this.artist?.products?.length > 0
    },

    hasEvents() {
      return this.artist?.events?.length > 0
    },

    sortOrderDescLabel() {
      if (this.localFilter.sortBy === 'price') {
        return this.$t('priceDesc')
      } else if (this.localFilter.sortBy === 'name') {
        return this.$t('nameDesc')
      }
      return this.$t('newestFirst')
    },

    sortOrderAscLabel() {
      if (this.localFilter.sortBy === 'price') {
        return this.$t('priceAsc')
      } else if (this.localFilter.sortBy === 'name') {
        return this.$t('nameAsc')
      }
      return this.$t('oldestFirst')
    },

    // Build route path for type filter
    shopBasePath() {
      return `/${this.artist?.urlAlias}/shop`
    },

    // Show category filter only when products type is explicitly selected
    showCategoryFilter() {
      return (!this.localFilter.type || this.localFilter.type === 'product') && this.availableCategories.length > 0
    },

    // Extract unique categories from all products
    availableCategories() {
      const categories = new Map()
      for (const product of this.artist?.products || []) {
        for (const cat of product.categories || []) {
          if (cat.slug && cat.name) {
            categories.set(cat.slug, { name: cat.name, slug: cat.slug })
          }
        }
      }
      return Array.from(categories.values())
    },

    // Get the currently active category from Vuex or route
    activeCategory() {
      // Check route param first (for direct URL access)
      if (this.$route.params.category) {
        return this.$route.params.category
      }
      // Fall back to Vuex state
      return this.shopFilter.category || null
    }
  },

  watch: {
    shopFilter: {
      handler(newFilter) {
        this.localFilter = { ...newFilter }
      },
      deep: true,
      immediate: true
    }
  },

  mounted() {
    this.localFilter = { ...this.shopFilter }

    // Listen for reset event
    this.$root.$on('resetAllShopFilters', this.handleReset)
  },

  beforeDestroy() {
    this.$root.$off('resetAllShopFilters', this.handleReset)
  },

  methods: {
    ...mapActions(['setShopFilter']),

    setType(type) {
      // Navigate to the corresponding route instead of just setting filter
      const routeMap = {
        null: '',
        subscription: '/subscriptions',
        product: '/products',
        event: '/events'
      }
      const targetPath = `${this.shopBasePath}${routeMap[type] || ''}`

      // Only navigate if we're on a different route
      if (this.$route.path !== targetPath) {
        this.$router.push(targetPath)
      }
    },

    setCategory(slug) {
      // Navigate to category route for SEO-friendly URLs
      if (slug) {
        // Navigate to specific category page
        const targetPath = `${this.shopBasePath}/products/${slug}`
        if (this.$route.path !== targetPath) {
          this.$router.push(targetPath)
        }
      } else {
        // Navigate back to all products
        const targetPath = `${this.shopBasePath}/products`
        if (this.$route.path !== targetPath) {
          this.$router.push(targetPath)
        }
      }
      // Also update Vuex for consistency
      this.setShopFilter({ category: slug })
      this.$emit('filtersChanged')
    },

    setSortBy(sortBy) {
      this.localFilter.sortBy = sortBy
      this.applyFilter()
    },

    setSortOrder(sortOrder) {
      this.localFilter.sortOrder = sortOrder
      this.applyFilter()
    },

    applyFilter() {
      this.setShopFilter(this.localFilter)
      this.$emit('filtersChanged')
    },

    handleReset() {
      // Navigate to main shop page (all items)
      const targetPath = this.shopBasePath
      if (this.$route.path !== targetPath) {
        this.$router.push(targetPath)
      }
      // Reset sort options and category
      this.localFilter = {
        type: null,
        sortBy: 'date',
        sortOrder: 'desc',
        category: null
      }
      this.setShopFilter(this.localFilter)
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'assets/sass/_variables';

.filter-shop-content {
  padding: 0;
}

.filter-section {
  margin-bottom: 30px;

  &:last-child {
    margin-bottom: 10px;
  }
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1rem;
  font-weight: 600;
  color: var(--body-background-text-color, #333);
  margin: 0 0 15px 0;

  i {
    color: var(--body-background-text-color, #333);
    font-size: 14px;
  }
}

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.filter-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  border-radius: 5px;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;

  // Default state - semi-transparent with text color
  background: color-mix(
    in hsl,
    var(--body-background-color, #fff),
    var(--body-background-text-color, #333) 8%
  );
  color: var(--body-background-text-color, #333);

  // Subtle inner glow effect
  box-shadow:
    inset 0 0 0 1px color-mix(
      in hsl,
      var(--body-background-color, #fff),
      var(--body-background-text-color, #333) 15%
    );

  &:hover {
    transform: translateY(-2px);
    box-shadow:
      inset 0 0 0 1px color-mix(
        in hsl,
        var(--body-background-color, #fff),
        var(--body-background-text-color, #333) 25%
      ),
      0 4px 12px color-mix(
        in hsl,
        var(--body-background-color, #fff),
        black 15%
      );
  }

  // Active state - filled with primary color
  &--active {
    background: var(--primary-color, #fb3e52);
    color: var(--primary-text-color, #fff);
    box-shadow:
      0 4px 15px color-mix(
        in hsl,
        var(--primary-color, #fb3e52),
        transparent 50%
      );

    &:hover {
      transform: translateY(-2px);
      box-shadow:
        0 6px 20px color-mix(
          in hsl,
          var(--primary-color, #fb3e52),
          transparent 40%
        );
    }
  }
}

// Mobile bottom sheet layout
@media screen and (max-width: $mobile) {
  .filter-section {
    margin-bottom: 24px;
  }

  .section-title {
    font-size: 0.95rem;
    margin-bottom: 12px;
  }

  .filter-chip {
    padding: 10px 18px;
    font-size: 0.9rem;
  }
}
</style>
